import { IocSelectorOption } from './../../../_shared/components/ioc-selector/ioc-selector.service';
import { ReturnerCourtSide } from './../../../_shared/models/returnerSide.model';
import { Player } from './../../../_shared/models/match.model';
import { Injectable, inject } from '@angular/core';
import { GameFormat } from '../../../_shared/models/game.model';
import { Match } from '../../../_shared/models/match.model';
import { Server } from '../../../_shared/models/point.model';
import {
  ScoreFormat,
  SetType,
} from '../../../_shared/models/score-format.model';
import { BoardHelpService } from '../board-help.service';
import { CourtSide } from '../../../_shared/models/enums/court-side';
import { IocSelectorService } from '../../../_shared/components/ioc-selector/ioc-selector.service';
import { StartMatchService } from '../../start-match/start-match.service';
import { MatchService } from '../../../_core/services/match.service';
import { ToastService } from '../../../_core/services/toast.service';

@Injectable({
  providedIn: 'root',
})
export class ServerService {
  iocSelectorService = inject(IocSelectorService);
  private startMatchService = inject(StartMatchService);
  private matchService = inject(MatchService);
  private toast = inject(ToastService);
  private bhs = inject(BoardHelpService);

  async changeServer(
    match: Match,
    scoreFormat: ScoreFormat,
    isNewSet = false
  ): Promise<{ server?: Server; suggestServerChange: boolean }> {
    // This is a hack to make time tennis matches to change servers for doubles
    return match.matchType === 'S' || scoreFormat.setType === SetType.TIMED
      ? this.getNextServerForSingles(match, isNewSet, scoreFormat)
      : await this.getNextServerForDoubles(match, scoreFormat);
  }

private getNextServerForSingles(
    match: Match,
    isNewSet: boolean,
    scoreFormat: ScoreFormat
  ): { server?: Server; suggestServerChange: boolean } {
    const lastPoint = this.bhs.getLastPoint(match);

    if (!lastPoint?.server?.sideNumber) {
      return { server: undefined, suggestServerChange: false };
    }

    const previousServer = isNewSet
      ? this.bhs.getNextToLastGame(match).points[0].server
      : lastPoint.server;

    let sideNumber = isNewSet ? this.bhs.getLastCompletedSet(match).winningSide : 0;

    if (isNewSet && !sideNumber) {
      const lastCompletedSet:any = this.bhs.getNextToLastGame(match)
      const lastTwoGamesPoints = lastCompletedSet.points.slice(-2);
      sideNumber = lastTwoGamesPoints[0]?.winningSide;
    }

    if (scoreFormat.setType !== SetType.TIMED) {
      sideNumber = previousServer?.sideNumber === 1 ? 2 : 1;
    }

    const returningSide = this.bhs.getReturningSide(lastPoint?.pointNumber || 0, match, scoreFormat);
    
    // Determine playerNumber based on scoreFormat.setType
    let playerNumber: number = 1; // assuming player 1 for singles match type

    const isTimedSet = scoreFormat.setType === SetType.TIMED;

    if (isTimedSet && isNewSet) {
      // For new TIMED set, keep last server's player number
      playerNumber = lastPoint?.server?.playerNumber || 1;
      sideNumber = lastPoint?.server?.sideNumber || 1;

    } else if (isTimedSet && !isNewSet) {
      // For ongoing TIMED set
      const currentWinningSide = lastPoint?.server?.sideNumber === 1 ? 2 : 1;
      const previousServerPlayerNumber = this.getLastServerPlayerNumberBySide(match, currentWinningSide);
      if (match?.matchType === 'D') {
        playerNumber = previousServerPlayerNumber
          ? previousServerPlayerNumber === 2 ? 1 : 2
          : (returningSide === CourtSide.DEUCE ? 1 : 2);
      }

    } else {
      // For NON-TIMED sets
      playerNumber = returningSide === CourtSide.DEUCE ? 1 : 2;
    }
    
    const isSideOne = previousServer?.sideNumber === 1;

    return {
      server: this.createServerObject(
        match,
        isSideOne ? 2 : 1,
        sideNumber,
        playerNumber,
        scoreFormat,
        returningSide,
        isNewSet,
        isTimedSet
      ),
      suggestServerChange: false,
    };
  }
 

  private createServerObject(
    match: Match,
    defaultSide: number,
    sideNumber: number,
    playerNumber: number,
    scoreFormat: ScoreFormat,
    returningSide: CourtSide,
    isNewSet: boolean = false,
    isTimedSet: boolean = false
  ): Server {
    const sideIndex = isNewSet && isTimedSet ? sideNumber - 1 : defaultSide - 1;
    return {
      sideNumber: sideNumber !== 0 ? sideNumber : defaultSide,
      playerNumber: scoreFormat.setType === SetType.TIMED ? playerNumber : 1,
      player: match.sides[sideIndex].players[playerNumber - 1]?.participant?._id || null,
      returningSide: returningSide,
    };
  }

  private async getNextServerForDoubles(
    match: Match,
    scoreFormat: ScoreFormat
  ): Promise<{ server?: Server; suggestServerChange: boolean }> {
    let suggestServerChange = false;
    const lastPoint = this.bhs.getLastPoint(match);

    if (!lastPoint?.server?.sideNumber) {
      return {
        server: undefined,
        suggestServerChange: false,
      };
    }

    const lastGame = this.bhs.getLastGame(match);
    const lastSet = this.bhs.getLastSet(match);
    const nextToLastGame = this.bhs.getNextToLastGame(match);
    const serverInTheGameThatJustFinished: Server =
      nextToLastGame?.points[0].server;
    const previouseGameWhereThisTeamServed =
      this.bhs.getNextToNextToLastGame(match);
    const serverInPreviouseGameWhereThisTeamServed =
      previouseGameWhereThisTeamServed?.points[0]?.server;
    const isLastSetTieBreak =
      (match.sets.length === 3 || match.sets.length === 5) &&
      lastSet?.games?.length === 1 &&
      (scoreFormat.lastSetTiebreakTo || 0) > 0;

    let sideNumber: number;
    let playerNumber: number;
    let playerId: string;
    const returningSide = this.bhs.getReturningSide(
      lastPoint.pointNumber,
      match,
      scoreFormat
    );

    if (
      match.sets.length === 1 &&
      lastSet?.games?.length === 2 &&
      nextToLastGame.points[0].server?.sideNumber
    ) {
      // CHOOSE SERVER IN THE SECOND GAME OF THE MATCH
      sideNumber = nextToLastGame.points[0].server?.sideNumber === 1 ? 2 : 1;

      if (nextToLastGame.points[0].server?.playerNumber) {
        suggestServerChange = true;
      }
    } else if (
      match.sets.length > 1 &&
      lastSet?.games?.length < 3 &&
      lastGame.points.length === 1
    ) {
      // CHOOSE THE SERVER IN THE 1ST AND 2ND GAME OF EACH SET AFTER THE FIRST SET
      sideNumber = serverInPreviouseGameWhereThisTeamServed.sideNumber;

      if (serverInTheGameThatJustFinished?.playerNumber) {
        playerNumber =
          serverInPreviouseGameWhereThisTeamServed.playerNumber === 2 ? 1 : 2;
        playerId =
          match.sides[sideNumber - 1].players[playerNumber - 1].participant._id;
        suggestServerChange = true;
        // Copy returner's sides from the previous set
        lastSet.returnerCourtSides =
          match.sets[match.sets.length - 2].returnerCourtSides;
      }
    } else if (lastGame.gameFormat === GameFormat.TIEBREAK) {
      if (
        match.sets.length === 1 &&
        lastSet?.games?.length === 1 &&
        (((scoreFormat.tiebreakType === 'TB5' ||
          scoreFormat.tiebreakType === 'TB7P') &&
          lastGame.points.length === 3) ||
          ((scoreFormat.tiebreakType === 'TB7' ||
            scoreFormat.tiebreakType === 'TB10') &&
            lastGame.points.length === 2))
      ) {
        // IF THIS IS 1 SET TIEBREAK MATCH
        const serverInThePointJustFinished =
          lastGame.points[lastGame.points.length - 2].server;
        sideNumber = serverInThePointJustFinished.sideNumber === 1 ? 2 : 1;

        if (serverInThePointJustFinished?.playerNumber) {
          suggestServerChange = true;
        }
      } else if (lastGame.points.length === 1) {
        // Get a server on 6-6 after 1 point is played
        // 1st point in the match tiebreak is handeled in the block above
        if (serverInPreviouseGameWhereThisTeamServed) {
          sideNumber = serverInPreviouseGameWhereThisTeamServed.sideNumber;
        }

        if (serverInTheGameThatJustFinished.playerNumber) {
          playerNumber = !serverInPreviouseGameWhereThisTeamServed?.playerNumber
            ? (await this.selectPlayerFromSide(match, sideNumber)).playerNumber // If there is no server information, ask server to input player number
            : serverInPreviouseGameWhereThisTeamServed.playerNumber === 2
            ? 1
            : 2; // Otherwise automatically change player

          playerId =
            match.sides[sideNumber - 1].players[playerNumber - 1].participant
              ._id;
        }
      } else if (lastGame.points.length === 2 || lastGame.points.length === 3) {
        // Handle the 2nd point of the tiebreak
        const serverInTheGameBeforeTiebreak = nextToLastGame?.points[0].server;
        sideNumber = serverInTheGameBeforeTiebreak.sideNumber;

        if (serverInTheGameBeforeTiebreak.playerNumber) {
          if (!serverInTheGameBeforeTiebreak || isLastSetTieBreak) {
            suggestServerChange = true;
          } else {
            playerNumber =
              serverInTheGameBeforeTiebreak.playerNumber === 2 ? 1 : 2;
            playerId =
              match.sides[sideNumber - 1].players[playerNumber - 1].participant
                ._id;
          }
        }
      } else {
        // Change server for all points after
        const previousServerForThisTeamInTiebreak =
          lastGame.points[lastGame.points.length - 4].server;
        sideNumber = previousServerForThisTeamInTiebreak.sideNumber;

        if (previousServerForThisTeamInTiebreak?.playerNumber) {
          playerNumber = !previousServerForThisTeamInTiebreak?.playerNumber
            ? (await this.selectPlayerFromSide(match, sideNumber)).playerNumber
            : previousServerForThisTeamInTiebreak.playerNumber === 2
            ? 1
            : 2;
          playerId =
            match.sides[sideNumber - 1].players[playerNumber - 1].participant
              ._id;
        }
      }
    } else {
      // Check the previous server in the set
      if (serverInTheGameThatJustFinished) {
        sideNumber = serverInPreviouseGameWhereThisTeamServed.sideNumber;

        if (serverInTheGameThatJustFinished.playerNumber) {
          playerNumber =
            serverInPreviouseGameWhereThisTeamServed.playerNumber === 2 ? 1 : 2;
          playerId =
            match.sides[sideNumber - 1].players[playerNumber - 1].participant
              ._id;
        }
      }
    }

    return {
      server: {
        sideNumber,
        playerNumber,
        player: playerId,
        returningSide,
      },
      suggestServerChange,
    };
  }

  async selectPlayerFromSide(
    match: Match,
    sideNumber: number,
    title = 'Select server'
  ): Promise<Player> {
    const players = match.sides[sideNumber - 1].players;
    const iocSelectorOptions: IocSelectorOption[] = [];
    players.forEach((player, i) => {
      iocSelectorOptions.push({
        label: this.bhs.getPlayersNames(match, sideNumber, 'full', i + 1)!,
        value: player,
      });
    });

    const showCancelButton = false;

    return await this.iocSelectorService.openPopover(
      title,
      iocSelectorOptions,
      showCancelButton
    );
  }

  getCurrentServer(match: Match): Server | null {
    const lastPoint = this.bhs.getLastPoint(match);

    if (!lastPoint?.server) {
      return null;
    }

    return {
      sideNumber: lastPoint.server.sideNumber,
      playerNumber: lastPoint.server.playerNumber,
      player: lastPoint.server.player,
      returningSide:
        lastPoint.server.returningSide === CourtSide.DEUCE
          ? CourtSide.AD
          : CourtSide.DEUCE,
    };
  }

  async getReturnerCourtSides(
    match: Match,
    returnSideNumber: number
  ): Promise<ReturnerCourtSide[]> {
    let returnerCourtSides =
      this.bhs.getLastSet(match)?.returnerCourtSides || [];

    returnerCourtSides = returnerCourtSides.filter((rcs) => {
      if (rcs.sideNumber !== returnSideNumber) {
        return rcs;
      }
    });

    const selectedReturner = await this.selectPlayerFromSide(
      match,
      returnSideNumber,
      'Select returner on Deuce'
    );
    const unselectedReturnerPlayerNumber =
      selectedReturner.playerNumber === 1 ? 2 : 1;

    returnerCourtSides.push({
      playerNumber: selectedReturner.playerNumber,
      sideNumber: returnSideNumber,
      player: selectedReturner.participant._id,
      courtSide: CourtSide.DEUCE,
    });

    returnerCourtSides.push({
      sideNumber: returnSideNumber,
      playerNumber: unselectedReturnerPlayerNumber,
      player:
        match.sides[returnSideNumber - 1].players[
          unselectedReturnerPlayerNumber - 1
        ]?.participant?._id,
      courtSide: CourtSide.AD,
    });

    return returnerCourtSides;
  }

  async selectServer(match: Match) {
    // TODO this is not updating UI
    const serverTrackerMode =
      await this.startMatchService.getServerTrackingMode(match);

    let selectedServer: Server;
    if (serverTrackerMode === 'individual') {
      selectedServer = await this.bhs.selectPlayer(match, true);
    } else if (serverTrackerMode === 'side') {
      const sideNumber = await this.bhs.selectSide(match, true);
      selectedServer = { sideNumber };
    } else if (serverTrackerMode === 'none') {
      this.bhs.getLastPoint(match).server = {};
      this.matchService.publishScoreUpdated(match);
    }

    if (serverTrackerMode !== 'none' && selectedServer?.sideNumber) {
      selectedServer.returningSide = this.bhs.getReturningSide(
        this.bhs.getLastPoint(match).pointNumber
      );
      this.bhs.getLastPoint(match).server = selectedServer;
      this.toast.presentToast('Server is changed successfully');
      this.matchService.publishScoreUpdated(match);
    }
  }

  private getLastServerPlayerNumberBySide(match, sideNumber) {
    if (!match || !Array.isArray(match.sets)) return null;
  
    const sets = match.sets;
    let lastServePlayerNumber = null;
  
    // Use label to break out of nested loops
    outerLoop:
    for (let i = sets.length - 1; i >= 0; i--) {
      const set = sets[i];
      if (!Array.isArray(set.games)) continue;
  
      for (let j = set.games.length - 1; j >= 0; j--) {
        const game = set.games[j];
        if (!Array.isArray(game.points)) continue;
  
        for (let k = game.points.length - 1; k >= 0; k--) {
          const point = game.points[k];
          const server = point?.server;
  
          if (server?.sideNumber === sideNumber) {
            lastServePlayerNumber = server.playerNumber || null;
            break outerLoop;
          }
        }
      }
    }
  
    return lastServePlayerNumber;
  }
}
